import * as d3 from 'd3';
import './d3-inflationcurvechart.css';
import { colors } from '../utils';

const signFormat = d3.format('+.1%');
const formatPercentage = d3.format(".0%");

export default function () {
  const margin = { top: 10, right: 80, bottom: 60, left: 50 };

  let width = 400;
  let height = 300;
  let svg;
  let _data;
  let volatilities;
  let inflationAdjustments;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let vol;
  let infAdj;

  const dispatch = d3.dispatch('selected');

  function chart(selection) {
    selection.each(function (data) {
      _data = data.data;
      volatilities = data.volatilities;
      inflationAdjustments = data.inflationAdjustments;

      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();

        const curve = g.append('g')
          .attr('class', 'inflationcurve-chart__curve');

        curve.append('path')
          .attr('class', 'inflationcurve-chart__curve--above');

        curve.append('path')
          .attr('class', 'inflationcurve-chart__curve--below');

        curve.append('path')
          .attr('class', 'inflationcurve-chart__curve--baseline');

        const rateGroup = curve.append('g')
          .attr('class', 'inflationcurve-chart__curve--rate');

        rateGroup.append('path')
          .attr('class', 'inflationcurve-chart__curve--rate-visible');

        rateGroup.append('path')
          .attr('class', 'inflationcurve-chart__curve--rate-invisible'); // Larger target for dragging

        rateGroup.append('text')
          .attr('class', 'inflationcurve-chart__curve--rate-text');

        createFocus();
        createAbove();
        createBelow();
        createLabels();
      }

      svg.attr('width', width).attr('height', height);

      drawAxes();
      drawCurve();
      drawFocus();
      drawAbove();
      drawBelow();
      drawLabels();
    });
  }

  function createScales() {
    const paddingFraction = 0.1;
    x = d3.scaleLinear().rangeRound([0, chartWidth]);
    y = d3.scaleLinear().rangeRound([chartHeight, 0]);

    const xExtent = d3.extent(_data.map(function (d) { return d.term; }));
    const yMax = d3.max(_data.map(function (d) { return d.rate; })) + volatilities[volatilities.length - 1] + inflationAdjustments[inflationAdjustments.length - 1];
    const yMin = d3.min(_data.map(function (d) { return d.rate; })) - volatilities[volatilities.length - 1] + inflationAdjustments[0];
    const yWidth = yMax - yMin;

    x.domain(xExtent);
    y.domain([yMin - paddingFraction * yWidth, yMax + paddingFraction * yWidth]).nice();
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'inflationcurve-chart__axis inflationcurve-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'inflationcurve-chart__axis inflationcurve-chart__axis--y')
  }

  function createFocus() {
    const focus = svg.select('g')
      .append('g')
      .attr('class', 'inflationcurve-chart__focus')
      .attr('cursor', 'ns-resize')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(8)
      .startAngle(0)
      .endAngle(2 * Math.PI);

    focus.append('path')
      .attr('d', arc())
      .attr('fill', colors.blue);

    focus.append('text')
      .attr('x', 10)
      .attr('dy', '.35em')
      .attr('font-size', '12px');

    function dragstarted() {
      d3.select(this).classed('inflationcurve-chart__focus--active', true);
    }

    function dragged() {
      const i = Math.floor(_data.length / 2);
      const baseline = _data[i].rate;

      const y0 = y.invert(d3.event.y);
      const y0Clipped = d3.max([d3.min([y0, baseline + 0.05]), baseline + -0.05]); // TODO: Calculate based on data

      dispatch.call('selected', chart, 'infAdj', y0Clipped - baseline);
    }

    function dragended() {
      d3.select(this).classed('inflationcurve-chart__focus--active', false);
    }
  }

  function createAbove() {
    const focus = svg.select('g')
      .append('g')
      .attr('class', 'inflationcurve-chart__above')
      .attr('cursor', 'ns-resize')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(8)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2);

    focus.append('path')
      .attr('class', 'inflationcurve-chart__above--left')
      .attr('d', arc())
      .attr('fill', colors.red);

    focus.append('path')
      .attr('class', 'inflationcurve-chart__above--right')
      .attr('d', arc())
      .attr('fill', colors.red);

    focus.append('text')
      .attr('x', 10)
      .attr('dy', '.35em')
      .attr('font-size', '12px')
      .text('+1σ');

    function dragstarted() {
      d3.select(this).classed('inflationcurve-chart__above--active', true);
    }

    function dragged() {
      const i = Math.floor(_data.length / 2);
      const baseline = _data[i].rate + infAdj;

      const y0 = y.invert(d3.event.y);
      const y0Clipped = d3.max([d3.min([y0, baseline + 0.048]), baseline]); // TODO: Calculate based on data
      dispatch.call('selected', chart, 'vol', y0Clipped - baseline);
    }

    function dragended() {
      d3.select(this).classed('inflationcurve-chart__above--active', false);
    }
  }

  function createBelow() {
    const focus = svg.select('g')
      .append('g')
      .attr('class', 'inflationcurve-chart__below')
      .attr('cursor', 'ns-resize')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(8)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2);

    focus.append('path')
      .attr('class', 'inflationcurve-chart__below--left')
      .attr('d', arc())
      .attr('fill', colors.red);

    focus.append('path')
      .attr('class', 'inflationcurve-chart__below--right')
      .attr('d', arc())
      .attr('fill', colors.red);

    focus.append('text')
      .attr('x', 10)
      .attr('dy', '.35em')
      .attr('font-size', '12px')
      .text('-1σ');

    function dragstarted() {
      d3.select(this).classed('inflationcurve-chart__below--active', true);
    }

    function dragged() {
      const i = Math.floor(_data.length / 2);
      const baseline = _data[i].rate + infAdj;

      const y0 = y.invert(d3.event.y);
      const y0Clipped = d3.max([d3.min([y0, baseline]), baseline - 0.048]); // TODO: Calculate based on data
      dispatch.call('selected', chart, 'vol', -(y0Clipped - baseline));
    }

    function dragended() {
      d3.select(this).classed('inflationcurve-chart__below--active', false);
    }
  }

  function createLabels() {
    svg.select('g')
      .append('text')
      .attr('class', 'inflationcurve-chart__label inflationcurve-chart__label--x')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Term (years)');

    svg.select('g')
      .append('text')
      .attr('class', 'inflationcurve-chart__label inflationcurve-chart__label--y')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Inflation');
  }

  function drawAxes() {
    const xAxis = svg.select('.inflationcurve-chart__axis--x');

    xAxis.attr('transform', 'translate(0,' + chartHeight + ')')
      .call(d3.axisBottom(x).ticks(5).tickSize(-chartHeight))
      .select('.domain').remove();

    xAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    xAxis.selectAll('text')
      .attr('dx', '-0.8em')
      .attr('dy', '.35em')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');

    const yAxis = svg.select('.inflationcurve-chart__axis--y')
      .call(d3.axisLeft(y).tickFormat(formatPercentage).tickSize(-chartWidth));

    yAxis.select('.domain').remove();

    yAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    yAxis.selectAll('.tick text')
      .attr('x', -6)
      .attr('dy', 4);
  }

  function drawCurve() {
    const baseline = d3.line()
      .curve(d3.curveBasis)
      .x(function (d) { return x(d.term); })
      .y(function (d) { return y(d.rate); });

    const rate = d3.line()
      .curve(d3.curveBasis)
      .x(function (d) { return x(d.term); })
      .y(function (d) { return y(d.rate + infAdj); });

    const above = d3.line()
      .curve(d3.curveBasis)
      .x(function (d) { return x(d.term); })
      .y(function (d) { return y(d.rate + infAdj + vol); });

    const below = d3.line()
      .curve(d3.curveBasis)
      .x(function (d) { return x(d.term); })
      .y(function (d) { return y(d.rate + infAdj - vol); });

    svg.select('.inflationcurve-chart__curve--baseline')
      .datum(_data)
      .attr('fill', 'none')
      .attr('stroke', colors.blue)
      .attr('stroke-width', 2)
      .attr('opacity', 0.2)
      .attr('d', baseline);

    svg.select('.inflationcurve-chart__curve--rate-invisible')
      .datum(_data)
      .attr('fill', 'none')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 20)
      .attr('d', rate)
      .attr('cursor', 'ns-resize')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    svg.select('.inflationcurve-chart__curve--rate-visible')
      .datum(_data)
      .attr('fill', 'none')
      .attr('stroke', colors.blue)
      .attr('stroke-width', 2)
      .attr('d', rate)
      .attr('cursor', 'ns-resize');

    function dragstarted() {
      d3.select(this).classed('inflationcurve-chart__focus--active', true);
    }

    function dragged() {
      const i = Math.floor(_data.length / 2);
      const baseline = _data[i].rate;

      const y0 = y.invert(d3.event.y);
      const y0Clipped = d3.max([d3.min([y0, baseline + 0.05]), baseline + -0.05]); // TODO: Calculate based on data

      dispatch.call('selected', chart, 'infAdj', y0Clipped - baseline);
    }

    function dragended() {
      d3.select(this).classed('inflationcurve-chart__focus--active', false);
    }

    svg.select('.inflationcurve-chart__curve--above')
      .datum(_data)
      .attr('fill', 'none')
      .attr('stroke', colors.red)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '10,5')
      .attr('d', above);

    svg.select('.inflationcurve-chart__curve--below')
      .datum(_data)
      .attr('fill', 'none')
      .attr('stroke', colors.red)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '10,5')
      .attr('d', below);
  }

  function drawFocus() {
    const middleIndex = Math.floor(_data.length / 2);
    const endIndex = _data.length - 1;

    svg.select('.inflationcurve-chart__focus').select('path')
      .attr('transform', 'translate(' + x(_data[middleIndex].term) + ',' + y(_data[middleIndex].rate + infAdj) + ')');

    const shiftLabel = Math.abs(infAdj) < 0.00005 ? 'current curve' : 'shift ' + signFormat(infAdj);

    svg.select('.inflationcurve-chart__focus').select('text')
      .attr('transform', 'translate(' + x(_data[endIndex].term) + ',' + y(_data[endIndex].rate + infAdj) + ')')
      .text(shiftLabel);
  }

  function drawAbove() {
    const leftIndex = Math.floor(_data.length / 4);
    const rightIndex = Math.floor(3 * _data.length / 4);
    const endIndex = _data.length - 1;

    const displayAbove = Math.abs(y(vol) - y(0)) > 12;

    const aboveGroup = svg.select('.inflationcurve-chart__above');

    aboveGroup.select('path.inflationcurve-chart__above--left')
      .attr('transform', 'translate(' + x(_data[leftIndex].term) + ',' + (y(_data[leftIndex].rate + infAdj + vol)) + ')');

    aboveGroup.select('path.inflationcurve-chart__above--right')
      .attr('transform', 'translate(' + x(_data[rightIndex].term) + ',' + (y(_data[rightIndex].rate + infAdj + vol)) + ')');

    aboveGroup.select('text')
      .attr('transform', 'translate(' + x(_data[endIndex].term) + ',' + y(_data[endIndex].rate + infAdj + vol) + ')')
      .attr('display', displayAbove ? 'block' : 'none')
      .text('+σ (' + (d3.format('.1%')(vol) + ')'));
    }

  function drawBelow() {
    const leftIndex = Math.floor(_data.length / 4);
    const rightIndex = Math.floor(3 * _data.length / 4);
    const endIndex = _data.length - 1;

    const displayBelow = Math.abs(y(vol) - y(0)) > 12;

    const belowGroup = svg.select('.inflationcurve-chart__below');

    belowGroup.select('path.inflationcurve-chart__below--left')
      .attr('transform', 'translate(' + x(_data[leftIndex].term) + ',' + (y(_data[leftIndex].rate + infAdj - vol)) + ')rotate(180)');

    belowGroup.select('path.inflationcurve-chart__below--right')
      .attr('transform', 'translate(' + x(_data[rightIndex].term) + ',' + (y(_data[rightIndex].rate + infAdj - vol)) + ')rotate(180)');

    belowGroup.select('text')
      .attr('transform', 'translate(' + x(_data[endIndex].term) + ',' + y(_data[endIndex].rate + infAdj - vol) + ')')
      .attr('display', displayBelow ? 'block' : 'none')
      .text('-σ (' + (d3.format('.1%')(vol) + ')'));
  }

  function drawLabels() {
    svg.select('.inflationcurve-chart__label--x')
      .attr('x', chartWidth / 2)
      .attr('y', chartHeight + margin.top + 37);

    svg.select('.inflationcurve-chart__label--y')
      .attr('transform', 'translate(-45, ' + chartHeight / 2 + ')rotate(-90)');
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.vol = function (value) {
    if (!arguments.length) return vol;
    vol = value;
    return chart;
  }

  chart.infAdj = function (value) {
    if (!arguments.length) return infAdj;
    infAdj = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-inflationcurvechart.js